import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Briefcase, MapPin, Target, Award, ArrowRight, ArrowLeft, CheckCircle, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';

const EXPERIENCE_LEVELS = [
  { value: 'entry', label: 'Entry Level', description: '0-2 years' },
  { value: 'mid', label: 'Mid Level', description: '3-5 years' },
  { value: 'senior', label: 'Senior Level', description: '6-10 years' },
  { value: 'executive', label: 'Executive', description: '10+ years' },
];

const JOB_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
];

const COMMON_SKILLS = [
  'JavaScript', 'Python', 'React', 'TypeScript', 'Node.js', 'SQL',
  'Java', 'AWS', 'Docker', 'Git', 'REST APIs', 'Agile',
  'Project Management', 'Communication', 'Leadership', 'Analytics',
  'Marketing', 'Sales', 'Customer Service', 'Design',
];

export function Onboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    job_title: '',
    experience_level: '',
    location: '',
    remote_preference: true,
    desired_job_types: [] as string[],
    skills: [] as string[],
    custom_skill: '',
    min_salary: '',
    max_salary: '',
  });

  useEffect(() => {
    // If user is not logged in, redirect to signup
    if (!user) {
      navigate('/signup');
      return;
    }

    // If profile is already complete, redirect to dashboard
    if (profile?.account_complete) {
      navigate('/swipe');
      return;
    }

    // Set initial step from URL param if provided
    const step = searchParams.get('step');
    if (step === 'profile') {
      setCurrentStep(1);
    }
  }, [user, profile, navigate, searchParams]);

  const totalSteps = 4;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    // Mark onboarding as skipped but not complete
    try {
      await supabase
        .from('profiles')
        .update({
          onboarding_skipped: true,
          account_complete: false
        })
        .eq('id', user!.id);

      navigate('/swipe');
    } catch (err) {
      console.error('Error skipping onboarding:', err);
      navigate('/swipe');
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');

    try {
      // Update profile with all collected information
      const updates: any = {
        job_title: formData.job_title || null,
        experience_level: formData.experience_level || null,
        location: formData.location || null,
        remote_preference: formData.remote_preference,
        desired_job_types: formData.desired_job_types.length > 0 ? formData.desired_job_types : null,
        skills: formData.skills.length > 0 ? formData.skills : null,
        min_salary: formData.min_salary ? parseInt(formData.min_salary) : null,
        max_salary: formData.max_salary ? parseInt(formData.max_salary) : null,
        account_complete: true,
        onboarding_completed_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user!.id);

      if (updateError) {
        console.error('Error updating profile:', updateError);
        setError('Failed to save profile. Please try again.');
        setLoading(false);
        return;
      }

      setLoading(false);
      navigate('/swipe');
    } catch (err: any) {
      console.error('Onboarding completion error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const toggleSkill = (skill: string) => {
    if (formData.skills.includes(skill)) {
      setFormData({
        ...formData,
        skills: formData.skills.filter((s) => s !== skill),
      });
    } else {
      setFormData({
        ...formData,
        skills: [...formData.skills, skill],
      });
    }
  };

  const addCustomSkill = () => {
    const skill = formData.custom_skill.trim();
    if (skill && !formData.skills.includes(skill)) {
      setFormData({
        ...formData,
        skills: [...formData.skills, skill],
        custom_skill: '',
      });
    }
  };

  const toggleJobType = (type: string) => {
    if (formData.desired_job_types.includes(type)) {
      setFormData({
        ...formData,
        desired_job_types: formData.desired_job_types.filter((t) => t !== type),
      });
    } else {
      setFormData({
        ...formData,
        desired_job_types: [...formData.desired_job_types, type],
      });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl mb-4">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to ResuSwipe!</h1>
          <p className="text-gray-600">Let's set up your profile to find the perfect job matches</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep} of {totalSteps}
            </span>
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Skip for now
            </button>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-orange-500 to-amber-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Job Title & Experience */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Briefcase className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Tell us about your career</h2>
                <p className="text-gray-600">What kind of role are you looking for?</p>
              </div>

              <div>
                <label htmlFor="job_title" className="block text-sm font-semibold text-gray-700 mb-2">
                  Desired Job Title
                </label>
                <input
                  type="text"
                  id="job_title"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                  placeholder="e.g., Software Engineer, Marketing Manager"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Experience Level
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {EXPERIENCE_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, experience_level: level.value })}
                      className={`p-4 border-2 rounded-xl text-left transition ${
                        formData.experience_level === level.value
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">{level.label}</div>
                      <div className="text-sm text-gray-600">{level.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Location & Remote Preference */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <MapPin className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Where do you want to work?</h2>
                <p className="text-gray-600">Let us know your location preferences</p>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                  Preferred Location
                </label>
                <input
                  type="text"
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                  placeholder="e.g., San Francisco, CA or Remote"
                />
              </div>

              <div>
                <label className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-xl cursor-pointer hover:border-orange-300 transition">
                  <input
                    type="checkbox"
                    checked={formData.remote_preference}
                    onChange={(e) => setFormData({ ...formData, remote_preference: e.target.checked })}
                    className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">Open to remote work</div>
                    <div className="text-sm text-gray-600">Show me remote job opportunities</div>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Job Types (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {JOB_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => toggleJobType(type.value)}
                      className={`p-3 border-2 rounded-xl font-medium transition ${
                        formData.desired_job_types.includes(type.value)
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Skills */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Award className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">What are your skills?</h2>
                <p className="text-gray-600">Select or add your top skills</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Skills
                </label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {COMMON_SKILLS.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => toggleSkill(skill)}
                      className={`px-4 py-2 rounded-full font-medium text-sm transition ${
                        formData.skills.includes(skill)
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {skill}
                      {formData.skills.includes(skill) && (
                        <CheckCircle className="w-3 h-3 inline ml-1" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="custom_skill" className="block text-sm font-semibold text-gray-700 mb-2">
                  Add Custom Skill
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="custom_skill"
                    value={formData.custom_skill}
                    onChange={(e) => setFormData({ ...formData, custom_skill: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomSkill();
                      }
                    }}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="Type a skill and press Enter"
                  />
                  <button
                    type="button"
                    onClick={addCustomSkill}
                    className="px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition"
                  >
                    Add
                  </button>
                </div>
              </div>

              {formData.skills.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Skills ({formData.skills.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm font-medium"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => toggleSkill(skill)}
                          className="hover:text-orange-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Salary Expectations */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Target className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Salary expectations</h2>
                <p className="text-gray-600">Help us find jobs that match your goals (optional)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="min_salary" className="block text-sm font-semibold text-gray-700 mb-2">
                    Minimum Salary
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      id="min_salary"
                      value={formData.min_salary}
                      onChange={(e) => setFormData({ ...formData, min_salary: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                      placeholder="50000"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="max_salary" className="block text-sm font-semibold text-gray-700 mb-2">
                    Maximum Salary
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      id="max_salary"
                      value={formData.max_salary}
                      onChange={(e) => setFormData({ ...formData, max_salary: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                      placeholder="80000"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-sm text-gray-700">
                  <strong className="text-orange-700">Note:</strong> Your salary expectations will help us match you
                  with relevant jobs but won't be shared with employers unless you apply.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="inline-flex items-center gap-2 px-6 py-3 text-gray-700 font-medium hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>

            <button
              onClick={handleNext}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-3 rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep === totalSteps ? (
                loading ? (
                  'Completing...'
                ) : (
                  'Complete Setup'
                )
              ) : (
                <>
                  Next
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
